import { useCallback, useEffect, useState } from 'react';
import { AlertNotifications } from '@bo-components/alerts/AlertNotifications';
import { Alerts } from '@bo-components/alerts/Alerts';
import { ChannelDetailsDrawer } from '@bo-components/alerts/ChannelDetailsDrawer';
import { ChannelHeader } from '@bo-components/alerts/ChannelHeader';
import { CreateAlertInput } from '@bo-components/alerts/CreateAlertInput';
import type { FormattedChannel } from '@library/types/alerts';
import { Spinner } from '@library/components/Spinner';
import { useChannels } from '@bo-hooks/alerts/useChannels';

export interface ChannelProps {
  isUserSubscribedCheck: boolean;
  page: number;
  setChannels: (channels: Array<FormattedChannel>) => void;
  setPage: (page: number) => void;
  setIsUserSubscribedCheck: (subscribeCheck: boolean) => void;
  selectedChannel: FormattedChannel;
  setSelectedChannel: (channel: FormattedChannel) => void;
}

export const Channel = ({
  isUserSubscribedCheck,
  page,
  setChannels,
  setPage,
  setIsUserSubscribedCheck,
  selectedChannel,
  setSelectedChannel
}: ChannelProps) => {
  const [channelDetailsDrawerOpen, setChannelDetailsDrawerOpen] =
    useState(false);

  const [searchChannelValue, setSearchChannelValue] = useState('');

  const { oneChannelData, channelFetching, channelDetails } = useChannels({
    channelId: selectedChannel?.id,
    page,
    query: searchChannelValue
  });

  const { displayName, id } = selectedChannel;

  const [specificAlertId, setSpecificAlertId] = useState('none');

  const handleCancelDrawer = useCallback(() => {
    setChannelDetailsDrawerOpen(false);
  }, [setChannelDetailsDrawerOpen]);

  const areNotificationsVisible =
    selectedChannel?.displayName.toLocaleLowerCase() === 'notifications';

  const handleOpenDrawer = useCallback(() => {
    setChannelDetailsDrawerOpen(true);
  }, []);

  useEffect(() => {
    setSearchChannelValue('');
  }, [selectedChannel]);

  return (
    <div className="text-soClean-110 bg-soFresh-110 border-soFresh-140 relative flex size-full flex-col overflow-hidden border-l border-solid">
      <ChannelHeader
        displayName={displayName}
        handleOpenDrawer={handleOpenDrawer}
        id={id}
        isUserSubscribedCheck={isUserSubscribedCheck}
        setChannels={setChannels}
        setIsUserSubscribedCheck={setIsUserSubscribedCheck}
        searchChannelValue={searchChannelValue}
        setSearchChannelValue={setSearchChannelValue}
        areNotificationsVisible={areNotificationsVisible}
      />
      {channelFetching && <Spinner className="self-center" />}
      {oneChannelData && !channelFetching && !areNotificationsVisible && (
        <>
          <Alerts
            alertsByDate={oneChannelData?.alerts}
            channelDetails={channelDetails}
            isFetching={channelFetching}
            selectedChannel={selectedChannel}
            specificAlertId={specificAlertId}
          />
          <CreateAlertInput
            page={page}
            setPage={setPage}
            totalPages={oneChannelData?.totalPages || 0}
          />
        </>
      )}
      {areNotificationsVisible && (
        <AlertNotifications
          setSelectedChannel={setSelectedChannel}
          setSpecificAlertId={setSpecificAlertId}
        />
      )}
      <ChannelDetailsDrawer
        channelDetails={channelDetails}
        drawerOpen={channelDetailsDrawerOpen}
        handleCancelDrawer={handleCancelDrawer}
      />
    </div>
  );
};
